import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { App, Button, Card, Form, Input, Modal, Popconfirm, Space, Table, Tooltip, Typography } from 'antd';
import { FilterOutlined, MergeCellsOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { NameCell } from '../../components/NameCell';
import type { SegmentItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage, api } from '../../api/client';
import { useDeleteSegment, useMergeSegments, useRefreshSegment, useSegments } from '../../features/audience/api';
import { EmptyState, tableLocale } from '../../components/states';
import { fmtDateTime, plural } from '../../utils/format';
import { useProjectColumn } from '../../components/ProjectSelect';
import { MentionTextArea } from '../../components/Mentions';

export function SegmentsPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const segments = useSegments();
  const remove = useDeleteSegment();
  const refresh = useRefreshSegment();
  const merge = useMergeSegments();
  const [selected, setSelected] = useState<string[]>([]);
  const [merging, setMerging] = useState(false);
  const [form] = Form.useForm<{ name: string; description?: string }>();
  const chosen = (segments.data ?? []).filter((s) => selected.includes(s.id));
  const [refreshingAll, setRefreshingAll] = useState(false);
  const projectColumn = useProjectColumn<SegmentItem>();
  const refreshAll = () => {
    setRefreshingAll(true);
    api
      .post('/segments/refresh-all')
      .then(() => message.success('Refresh queued. Counts update in a moment.'))
      .catch((e) => message.error(errorMessage(e)))
      .finally(() => setRefreshingAll(false));
  };

  const openMerge = () => {
    form.setFieldsValue({ name: chosen.map((s) => s.name).join(' + ').slice(0, 120), description: '' });
    setMerging(true);
  };
  const doMerge = async (v: { name: string; description?: string }) => {
    try {
      const s = await merge.mutateAsync({ segmentIds: chosen.map((c) => c.id), name: v.name.trim(), description: v.description?.trim() || null });
      message.success(`Merged into "${s.name}" with ${plural(s.memberCount, 'member')}`);
      if (!s.dynamic) message.info("Their rules couldn't be combined into one rule set, so the new segment keeps its members by hand. Add rules in the segment if you want it to update itself.", 8);
      setMerging(false);
      setSelected([]);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const membersFilter = (id: string) => encodeURIComponent(JSON.stringify({ combinator: 'and', conditions: [{ kind: 'segment', op: 'in_any', segmentIds: [id] }] }));

  return (
    <div className="page">
      <PageHeader
        title="Segments"
        subtitle="Saved rules that update automatically. Membership is recomputed every 15 minutes and when rules change."
        extra={
          <Space>
            <Tooltip title={selected.length < 2 ? 'Tick two or more segments in the table to merge them' : undefined}>
              <Button icon={<MergeCellsOutlined />} disabled={selected.length < 2} onClick={openMerge}>
                {selected.length >= 2 ? `Merge ${selected.length} segments` : 'Merge'}
              </Button>
            </Tooltip>
            <Tooltip title="Queue a refresh of every segment on the background worker">
              <Button icon={<ReloadOutlined />} loading={refreshingAll} disabled={!segments.data?.length} onClick={refreshAll}>
                Refresh all
              </Button>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/segments/new')}>
              New segment
            </Button>
          </Space>
        }
      />
      <Card>
      <Table<SegmentItem>
        rowKey="id"
        loading={segments.isLoading}
        locale={tableLocale(segments, <EmptyState description="No segments yet. A segment groups contacts by rules, such as lifecycle stage or tag, and stays up to date." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/segments/new')}>New segment</Button>} />)}
        dataSource={segments.data ?? []}
        pagination={false}
        rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) }}
        columns={[
          { title: 'Segment', dataIndex: 'name', render: (v, s) => <NameCell name={v} sub={s.description ?? 'No description'} to={`/segments/${s.id}`} icon={<FilterOutlined />} /> },
          ...projectColumn,
          { title: 'Rules', key: 'rules', width: 90, render: (_, s) => s.rules.conditions.length },
          { title: 'Members', dataIndex: 'memberCount', width: 110, render: (n: number, s) => (n ? <Link to={`/contacts?filter=${membersFilter(s.id)}`}>{n.toLocaleString()}</Link> : 0) },
          { title: 'Last evaluated', dataIndex: 'lastEvaluatedAt', width: 170, render: (v) => (v ? fmtDateTime(v) : 'Never') },
          {
            key: 'actions',
            align: 'right',
            render: (_, s) => (
              <Space>
                <Button size="small" loading={refresh.isPending && refresh.variables === s.id} onClick={() => refresh.mutateAsync(s.id).then((r) => message.success(`${plural(r.memberCount, 'member')}: ${r.entered} entered, ${r.left} left`)).catch((e) => message.error(errorMessage(e)))}>
                  Refresh
                </Button>
                <Popconfirm title={`Delete segment "${s.name}"?`} description="Contacts are kept. Campaigns that target it lose this audience." okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => remove.mutateAsync(s.id).then(() => message.success('Segment deleted')).catch((e) => message.error(errorMessage(e)))}>
                  <Button size="small" danger>Delete</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      </Card>
      <Modal
        title={`Merge ${plural(chosen.length, 'segment')}`}
        open={merging}
        onCancel={() => setMerging(false)}
        onOk={() => form.submit()}
        okText="Merge"
        confirmLoading={merge.isPending}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          A new segment gets every contact in {chosen.map((s) => `"${s.name}"`).join(', ')}. A contact in more than one is counted once. The original segments are
          deleted, and campaigns not sent yet and other segments that used them switch to the new one.
        </Typography.Paragraph>
        <Form form={form} layout="vertical" onFinish={doMerge} requiredMark={false}>
          <Form.Item name="name" label="Name of the merged segment" rules={[{ required: true, whitespace: true, message: 'Give the segment a name' }]}>
            <Input autoFocus maxLength={120} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <MentionTextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={1000} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
